import { useMemo } from 'react';
import { MRT_ColumnDef } from 'material-react-table';
import { Box, Chip, Typography } from '@mui/material';

import { GeneralTable } from '@/shared/components/ui';
import { useTableFilter } from '@/shared';
import { useFetchClientesAfectados } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-masivos.actions';

type ClienteAfectado = {
  id_contrato: string;
  codigo: string;
  direccion: string | null;
  ipv4_actual: string | null;
  nap_codigo: string | null;
  olt_codigo: string | null;
  plan_nombre: string | null;
  estado_servicio: string | null;
  celular: string | null;
  cliente: {
    id_cliente: string;
    identificacion: string | null;
    razon_social: string | null;
    celular: string | null;
  } | null;
};

export type ListaClientesAfectadosProps = {
  masivoId: string;
};

const ListaClientesAfectados: React.FC<ListaClientesAfectadosProps> = ({
  masivoId,
}) => {
  const { pagination, setPagination } = useTableFilter();

  const page = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const {
    data: clientesData,
    isLoading,
    isRefetching,
  } = useFetchClientesAfectados(masivoId, { page, page_size: pageSize });

  const body: any = clientesData ?? {};
  const clientes: ClienteAfectado[] = body.data ?? [];
  const totalClientes: number = body.total ?? 0;

  const columns = useMemo<MRT_ColumnDef<ClienteAfectado>[]>(
    () => [
      {
        id: 'identificacion',
        header: 'Cedula',
        size: 130,
        accessorFn: (row) => row.cliente?.identificacion ?? '',
      },
      {
        id: 'razon_social',
        header: 'Nombre Completo',
        size: 220,
        accessorFn: (row) => row.cliente?.razon_social ?? '',
      },
      {
        id: 'contrato_codigo',
        header: 'Contrato',
        size: 140,
        accessorFn: (row) => row.codigo ?? '',
      },
      {
        id: 'estado_servicio',
        header: 'Estado Servicio',
        size: 130,
        accessorFn: (row) => row.estado_servicio ?? '',
        Cell: ({ cell }) => {
          const estado = cell.getValue<string>();
          return (
            <Chip
              label={estado || 'N/A'}
              size="small"
              color={estado === 'ACTIVO' ? 'success' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'direccion',
        header: 'Direccion',
        size: 250,
      },
      {
        accessorKey: 'nap_codigo',
        header: 'NAP',
        size: 120,
      },
      {
        accessorKey: 'ipv4_actual',
        header: 'IPv4',
        size: 130,
      },
      {
        id: 'plan',
        header: 'Plan',
        size: 140,
        accessorFn: (row) => row.plan_nombre ?? '',
      },
    ],
    [],
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Total clientes afectados: {totalClientes}
      </Typography>

      <GeneralTable<ClienteAfectado>
        columns={columns}
        data={clientes}
        isLoading={isLoading}
        isRefetching={isRefetching}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={totalClientes}
        canEdit={false}
        canDelete={false}
        enableGlobalFilter={false}
      />
    </Box>
  );
};

export default ListaClientesAfectados;
